import { useState, useEffect, useRef } from 'react';
import Layout from '@/components/Layout';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Camera, Play, Square, Save, Plus, Clock, Bell, FileText, Video, TrendingUp, Users, Check, X, UserCheck, Trash2, Mic, Archive, ArchiveRestore, Upload } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import VoiceInput from '@/components/VoiceInput';
import usePermissions from '@/hooks/usePermissions';
import { ReadOnlyBanner, NoAccessScreen } from '@/components/PermissionUI';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const fmtDrill = (d) => `${d.duration} min${d.seconds ? ` ${d.seconds} sec` : ''}`;
const API = `${BACKEND_URL}/api`;

export default function PracticePage({ user, onLogout, permissions }) {
  const { canEdit, canView, isReadOnly } = usePermissions(permissions);
  const [practices, setPractices] = useState([]);
  const [practicePlans, setPracticePlans] = useState([]);
  const [scoutingReports, setScoutingReports] = useState([]);
  const [practiceVideos, setPracticeVideos] = useState([]);
  const [archivedVideos, setArchivedVideos] = useState([]);
  const [recordingView, setRecordingView] = useState('active'); // 'active' | 'archived'
  
  // Attendance state
  const [players, setPlayers] = useState([]);
  const [attendance, setAttendance] = useState({});
  const [attendanceDate, setAttendanceDate] = useState(new Date().toISOString().split('T')[0]);
  const [isSavingAttendance, setIsSavingAttendance] = useState(false);
  
  // Timer state
  const [timerMinutes, setTimerMinutes] = useState(30);
  const [timerSeconds, setTimerSeconds] = useState(0);
  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const [alarmEnabled, setAlarmEnabled] = useState(true);
  const timerRef = useRef(null);
  const audioRef = useRef(null);
  
  // Drill-by-drill timer state
  const [activePlan, setActivePlan] = useState(null);
  const [currentDrillIndex, setCurrentDrillIndex] = useState(0);
  const [isDrillMode, setIsDrillMode] = useState(false);
  
  // Recording state
  const [isRecording, setIsRecording] = useState(false);
  const [recordedChunks, setRecordedChunks] = useState([]);
  const [mediaStream, setMediaStream] = useState(null);
  const [recordingTitle, setRecordingTitle] = useState('');
  const mediaRecorderRef = useRef(null);
  const videoPreviewRef = useRef(null);
  
  // Dialog states
  const [showAddPlanDialog, setShowAddPlanDialog] = useState(false);
  const [showAddReportDialog, setShowAddReportDialog] = useState(false);
  const [showRecordDialog, setShowRecordDialog] = useState(false);
  
  const [newPlan, setNewPlan] = useState({ 
    title: '', 
    description: '', 
    drills: [],
    objectives: '',
    focus_area: '',
    total_duration: 60
  });
  const [newReport, setNewReport] = useState({ opponent_name: '', strengths: '', weaknesses: '', game_plan: '' });

  useEffect(() => {
    loadPracticeData();
    loadPlayers();
    return () => {
      if (mediaStream) {
        mediaStream.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  useEffect(() => {
    if (attendanceDate) {
      loadAttendance(attendanceDate);
    }
  }, [attendanceDate]);

  useEffect(() => {
    if (isTimerRunning) {
      timerRef.current = setInterval(() => {
        setTimerSeconds(prevSeconds => {
          if (prevSeconds === 0) {
            setTimerMinutes(prevMinutes => {
              if (prevMinutes === 0) {
                // Timer finished for current drill
                clearInterval(timerRef.current);
                setIsTimerRunning(false);
                
                // If in drill mode, move to next drill
                if (isDrillMode && activePlan?.drills) {
                  const nextIndex = currentDrillIndex + 1;
                  if (nextIndex < activePlan.drills.length) {
                    const nextDrill = activePlan.drills[nextIndex];
                    setCurrentDrillIndex(nextIndex);
                    setTimerMinutes(nextDrill.duration);
                    setTimerSeconds(nextDrill.seconds || 0);
                    toast.success(`Starting: ${nextDrill.name} (${fmtDrill(nextDrill)})`);
                    if (alarmEnabled && audioRef.current) {
                      audioRef.current.play();
                    }
                    // Auto-start next drill after a brief pause
                    setTimeout(() => setIsTimerRunning(true), 1500);
                    return nextDrill.duration;
                  } else {
                    // All drills complete
                    setIsDrillMode(false);
                    setActivePlan(null);
                    setCurrentDrillIndex(0);
                    toast.success('🎉 Practice plan complete!');
                    if (alarmEnabled && audioRef.current) {
                      audioRef.current.play();
                    }
                    return 0;
                  }
                } else {
                  // Regular timer finished
                  if (alarmEnabled && audioRef.current) {
                    audioRef.current.play();
                  }
                  toast.success('Timer finished!');
                  return 0;
                }
              } else {
                return prevMinutes - 1;
              }
            });
            return 59;
          }
          return prevSeconds - 1;
        });
      }, 1000);
    } else if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    return () => clearInterval(timerRef.current);
  }, [isTimerRunning, alarmEnabled, isDrillMode, activePlan, currentDrillIndex]);

  const loadPlayers = async () => {
    try {
      const response = await axios.get(`${API}/players?team_id=${user.team_id}`);
      setPlayers(response.data);
      // Initialize attendance state for all players as absent
      const initialAttendance = {};
      response.data.forEach(player => {
        initialAttendance[player.id] = false;
      });
      setAttendance(initialAttendance);
    } catch (error) {
      console.error('Failed to load players');
    }
  };

  const loadAttendance = async (date) => {
    try {
      const response = await axios.get(`${API}/attendance?team_id=${user.team_id}&date=${date}`);
      if (response.data && response.data.records) {
        const loadedAttendance = {};
        players.forEach(player => {
          loadedAttendance[player.id] = false;
        });
        response.data.records.forEach(record => {
          loadedAttendance[record.player_id] = record.present;
        });
        setAttendance(loadedAttendance);
      }
    } catch (error) {
      console.error('Failed to load attendance');
    }
  };

  const planFileRef = useRef(null);
  const [isImportingPlan, setIsImportingPlan] = useState(false);
  const handlePlanImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIsImportingPlan(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/practice-plans/import?team_id=${user.team_id}&dry_run=true`, fd);
      const plans = res.data.plans || [];
      if (!plans.length) { toast.error('No practice plan content found in the file'); return; }
      // Review before saving: first plan opens in the editor, the rest queue up behind it.
      setImportQueue(plans.slice(1));
      setImportTotal(plans.length);
      loadPlanIntoEditor(plans[0], true);
      toast.success(`${plans.length} plan${plans.length === 1 ? '' : 's'} read from ${file.name} — review and edit, then CREATE PLAN`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    } finally {
      setIsImportingPlan(false);
      e.target.value = '';
    }
  };

  const [importQueue, setImportQueue] = useState([]);
  const [importTotal, setImportTotal] = useState(0);
  const [editingImport, setEditingImport] = useState(false);
  const loadPlanIntoEditor = (plan, fromImport) => {
    setNewPlan({
      title: plan.title || '', description: plan.description || '', objectives: plan.objectives || '', focus_area: plan.focus_area || '',
      drills: (plan.drills || []).map((d) => ({ name: d.name || '', duration: Number(d.duration) || 0, seconds: Number(d.seconds) || 0, type: d.type === 'break' ? 'break' : 'drill' })),
      total_duration: plan.total_duration || 0,
    });
    setEditingImport(Boolean(fromImport));
    setShowAddPlanDialog(true);
  };
  const skipImportedPlan = () => {
    if (importQueue.length) { loadPlanIntoEditor(importQueue[0], true); setImportQueue(importQueue.slice(1)); }
    else { setEditingImport(false); setImportTotal(0); setShowAddPlanDialog(false); setNewPlan({ title: '', description: '', drills: [], objectives: '', focus_area: '', total_duration: 60 }); }
  };

  const loadPracticeData = async () => {
    try {
      const [plansRes, reportsRes, videosRes, archivedRes] = await Promise.all([
        axios.get(`${API}/practice-plans?team_id=${user.team_id}`),
        axios.get(`${API}/scouting-reports?team_id=${user.team_id}`),
        axios.get(`${API}/videos?team_id=${user.team_id}&video_type=practice`),
        axios.get(`${API}/videos?team_id=${user.team_id}&video_type=practice&include_archived=true`),
      ]);
      setPracticePlans(plansRes.data);
      setScoutingReports(reportsRes.data);
      setPracticeVideos(videosRes.data);
      // include_archived=true returns BOTH — filter to only archived so the tab shows what was hidden
      setArchivedVideos((archivedRes.data || []).filter((v) => v.archived));
    } catch (error) {
      console.error('Failed to load practice data');
    }
  };

  const restorePracticeVideo = async (videoId) => {
    try {
      await axios.put(`${API}/videos/${videoId}`, { archived: false });
      toast.success('Recording restored');
      loadPracticeData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to restore recording');
    }
  };

  const deletePracticePlan = async (planId) => {
    if (!window.confirm('Are you sure you want to delete this practice plan?')) {
      return;
    }
    try {
      await axios.delete(`${API}/practice-plans/${planId}`);
      toast.success('Practice plan deleted');
      loadPracticeData();
    } catch (error) {
      toast.error('Failed to delete practice plan');
    }
  };

  const toggleAttendance = (playerId) => {
    setAttendance(prev => ({
      ...prev,
      [playerId]: !prev[playerId]
    }));
  };

  const markAllPresent = () => {
    const allPresent = {};
    players.forEach(player => {
      allPresent[player.id] = true;
    });
    setAttendance(allPresent);
  };

  const markAllAbsent = () => {
    const allAbsent = {};
    players.forEach(player => {
      allAbsent[player.id] = false;
    });
    setAttendance(allAbsent);
  };

  const saveAttendance = async () => {
    setIsSavingAttendance(true);
    try {
      const records = Object.entries(attendance).map(([playerId, present]) => ({
        player_id: playerId,
        present
      }));
      
      await axios.post(`${API}/attendance`, {
        team_id: user.team_id,
        date: attendanceDate,
        records,
        recorded_by: user.id
      });
      
      const presentCount = Object.values(attendance).filter(v => v).length;
      toast.success(`Attendance saved! ${presentCount}/${players.length} players present`);
    } catch (error) {
      toast.error('Failed to save attendance');
    } finally {
      setIsSavingAttendance(false);
    }
  };

  const startTimer = () => {
    setIsTimerRunning(true);
  };

  const pauseTimer = () => {
    setIsTimerRunning(false);
  };

  const resetTimer = () => {
    setIsTimerRunning(false);
    setTimerMinutes(30);
    setTimerSeconds(0);
    setIsDrillMode(false);
    setActivePlan(null);
    setCurrentDrillIndex(0);
  };

  // Start drill-by-drill timer from a practice plan
  const startDrillTimer = (plan) => {
    if (!plan.drills || plan.drills.length === 0) {
      toast.error('No drills in this plan');
      return;
    }
    
    // Reset and setup for drill mode
    setIsTimerRunning(false);
    setActivePlan(plan);
    setCurrentDrillIndex(0);
    setIsDrillMode(true);
    
    // Set timer to first drill duration
    const firstDrill = plan.drills[0];
    setTimerMinutes(firstDrill.duration);
    setTimerSeconds(firstDrill.seconds || 0);
    
    toast.success(`Starting plan: ${plan.title} - ${firstDrill.name} (${fmtDrill(firstDrill)})`);
    
    // Auto-start after brief delay
    setTimeout(() => setIsTimerRunning(true), 500);
  };

  // Skip to next drill
  const skipToNextDrill = () => {
    if (!isDrillMode || !activePlan?.drills) return;
    
    const nextIndex = currentDrillIndex + 1;
    if (nextIndex < activePlan.drills.length) {
      setIsTimerRunning(false);
      setCurrentDrillIndex(nextIndex);
      const nextDrill = activePlan.drills[nextIndex];
      setTimerMinutes(nextDrill.duration);
      setTimerSeconds(nextDrill.seconds || 0);
      toast.success(`Skipped to: ${nextDrill.name} (${fmtDrill(nextDrill)})`);
    } else {
      // No more drills
      setIsDrillMode(false);
      setActivePlan(null);
      setCurrentDrillIndex(0);
      toast.success('Practice plan complete!');
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      setMediaStream(stream);
      
      if (videoPreviewRef.current) {
        videoPreviewRef.current.srcObject = stream;
      }

      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      const chunks = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunks.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        setRecordedChunks(chunks);
      };

      mediaRecorder.start();
      setIsRecording(true);
      toast.success('Recording started');
    } catch (error) {
      toast.error('Failed to start recording');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (mediaStream) {
        mediaStream.getTracks().forEach(track => track.stop());
      }
      toast.success('Recording stopped');
    }
  };

  const saveRecording = async () => {
    if (recordedChunks.length === 0) {
      toast.error('No recording to save');
      return;
    }

    const blob = new Blob(recordedChunks, { type: 'video/webm' });
    const formData = new FormData();
    formData.append('file', blob, 'practice_recording.webm');
    formData.append('title', recordingTitle || 'Practice Recording');
    formData.append('description', 'In-app practice recording');
    formData.append('video_type', 'practice');
    formData.append('team_id', user.team_id);

    try {
      const response = await axios.post(`${API}/videos/record`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      // Trigger AI analysis
      await axios.post(`${API}/videos/${response.data.id}/analyze`);
      
      toast.success('Recording saved and queued for AI analysis!');
      setShowRecordDialog(false);
      setRecordedChunks([]);
      setRecordingTitle('');
      if (videoPreviewRef.current) {
        videoPreviewRef.current.srcObject = null;
      }
      loadPracticeData();
    } catch (error) {
      toast.error('Failed to save recording');
    }
  };

  const deletePracticeVideo = async (videoId) => {
    if (!window.confirm('Delete this practice recording? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/videos/${videoId}`);
      toast.success('Recording deleted');
      loadPracticeData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete recording');
    }
  };

  const archivePracticeVideo = async (videoId) => {
    try {
      await axios.put(`${API}/videos/${videoId}`, { archived: true });
      toast.success('Recording archived');
      loadPracticeData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to archive recording');
    }
  };

  const handleAddPlan = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/practice-plans`, { 
        ...newPlan, 
        team_id: user.team_id,
        total_duration: newPlan.drills.reduce((sum, d) => sum + d.duration, 0)
      });
      toast.success('Practice plan added!');
      loadPracticeData();
      if (editingImport && importQueue.length) { loadPlanIntoEditor(importQueue[0], true); setImportQueue(importQueue.slice(1)); return; }
      setEditingImport(false); setImportTotal(0);
      setShowAddPlanDialog(false);
      setNewPlan({ title: '', description: '', drills: [], objectives: '', focus_area: '', total_duration: 60 });
    } catch (error) {
      toast.error('Failed to add practice plan');
    }
  };

  const printPlan = (plan) => {
    const printWindow = window.open('', '_blank');
    const drillsHtml = plan.drills?.map((drill, idx) => 
      `<tr style="background: ${drill.type === 'break' ? '#fef9c3' : 'white'}">
        <td style="border: 1px solid #ddd; padding: 8px;">${idx + 1}</td>
        <td style="border: 1px solid #ddd; padding: 8px;">${drill.name}</td>
        <td style="border: 1px solid #ddd; padding: 8px; text-align: center;">${drill.duration} min</td>
      </tr>`
    ).join('') || '';
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Practice Plan - ${plan.title}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { font-family: Arial, sans-serif; padding: 30px; }
            .header { text-align: center; margin-bottom: 20px; border-bottom: 3px solid #1e3a8a; padding-bottom: 15px; }
            .header h1 { font-size: 28px; color: #1e3a8a; text-transform: uppercase; }
            .meta { display: flex; justify-content: space-between; margin-bottom: 20px; background: #f8fafc; padding: 10px; border-radius: 8px; }
            .meta div { text-align: center; }
            .meta label { font-size: 12px; color: #666; display: block; }
            .meta span { font-size: 16px; font-weight: bold; }
            table { width: 100%; border-collapse: collapse; margin: 20px 0; }
            th { background: #1e3a8a; color: white; padding: 10px; text-align: left; }
            td { border: 1px solid #ddd; padding: 8px; }
            .section { margin: 15px 0; }
            .section-title { font-weight: bold; color: #1e3a8a; margin-bottom: 5px; }
            .footer { text-align: center; margin-top: 30px; padding-top: 15px; border-top: 1px solid #ddd; font-size: 11px; color: #888; }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>🏀 ${plan.title}</h1>
            <p>Practice Plan - PAC (Personal AI Coach)</p>
          </div>
          <div class="meta">
            <div>
              <label>Focus Area</label>
              <span>${plan.focus_area?.replace('_', ' ').toUpperCase() || 'General'}</span>
            </div>
            <div>
              <label>Total Duration</label>
              <span>${plan.drills?.reduce((sum, d) => sum + d.duration, 0) || 0} minutes</span>
            </div>
            <div>
              <label>Drills</label>
              <span>${plan.drills?.length || 0}</span>
            </div>
          </div>
          ${plan.objectives ? `<div class="section"><div class="section-title">OBJECTIVES</div><p>${plan.objectives}</p></div>` : ''}
          ${plan.drills?.length > 0 ? `
            <table>
              <thead>
                <tr>
                  <th style="width: 40px">#</th>
                  <th>Drill / Activity</th>
                  <th style="width: 100px; text-align: center">Duration</th>
                </tr>
              </thead>
              <tbody>${drillsHtml}</tbody>
            </table>
          ` : ''}
          ${plan.description ? `<div class="section"><div class="section-title">NOTES</div><p>${plan.description}</p></div>` : ''}
          <div class="footer">Generated by PAC (Personal AI Coach) • ${new Date().toLocaleString()}</div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const handleAddReport = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/scouting-reports`, { ...newReport, team_id: user.team_id });
      toast.success('Scouting report added!');
      setShowAddReportDialog(false);
      setNewReport({ opponent_name: '', strengths: '', weaknesses: '', game_plan: '' });
      loadPracticeData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add scouting report');
    }
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-8 space-y-8" data-testid="practice-page">
        {!canView('practice_plans') ? (
          <NoAccessScreen area="Practice" />
        ) : (
        <>
        {isReadOnly('practice_plans') && <ReadOnlyBanner area="Practice" />}
        <div className="border-b border-border pb-6">
          <h1 className="font-heading text-5xl font-black uppercase tracking-tighter text-primary">
            Practice Management
          </h1>
          <p className="text-muted-foreground mt-2">Plan, record, and analyze your team practices</p>
        </div>

        {/* Attendance Section */}
        <Card className="border-2 border-primary/50 bg-primary/5">
          <CardHeader className="border-b bg-primary/10">
            <div className="flex items-center justify-between">
              <CardTitle className="font-heading uppercase text-2xl flex items-center gap-2">
                <UserCheck className="h-6 w-6" />
                Practice Attendance
              </CardTitle>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Label className="text-sm">Date:</Label>
                  <Input
                    type="date"
                    value={attendanceDate}
                    onChange={(e) => setAttendanceDate(e.target.value)}
                    className="w-40"
                    data-testid="attendance-date"
                  />
                </div>
                <Badge variant="outline" className="text-lg px-3 py-1">
                  {Object.values(attendance).filter(v => v).length}/{players.length} Present
                </Badge>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-6">
            {players.length === 0 ? (
              <p className="text-center text-muted-foreground py-4">
                No players in roster. Add players to take attendance.
              </p>
            ) : (
              <>
                <div className="flex gap-2 mb-4">
                  <Button variant="outline" size="sm" onClick={markAllPresent} data-testid="mark-all-present">
                    <Check className="h-4 w-4 mr-1" />
                    Mark All Present
                  </Button>
                  <Button variant="outline" size="sm" onClick={markAllAbsent} data-testid="mark-all-absent">
                    <X className="h-4 w-4 mr-1" />
                    Mark All Absent
                  </Button>
                </div>
                
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
                  {players.map((player) => (
                    <button
                      key={player.id}
                      onClick={() => toggleAttendance(player.id)}
                      className={`p-3 rounded-lg border-2 transition-all text-left ${
                        attendance[player.id]
                          ? 'bg-green-100 border-green-500 hover:bg-green-200'
                          : 'bg-red-50 border-red-300 hover:bg-red-100'
                      }`}
                      data-testid={`attendance-${player.id}`}
                    >
                      <div className="flex items-center gap-2">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                          attendance[player.id] ? 'bg-green-500 text-white' : 'bg-red-400 text-white'
                        }`}>
                          {player.jersey_number || '?'}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-sm truncate">
                            {player.first_name} {player.last_name?.charAt(0)}.
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {attendance[player.id] ? '✓ Present' : '✗ Absent'}
                          </p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>

                <div className="mt-6 flex justify-end">
                  <Button 
                    onClick={saveAttendance} 
                    disabled={isSavingAttendance}
                    data-testid="save-attendance-btn"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    {isSavingAttendance ? 'SAVING...' : 'SAVE ATTENDANCE'}
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Timer and Recording Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Interval Timer */}
          <Card className="border-2">
            <CardHeader className="border-b bg-muted/30">
              <CardTitle className="font-heading uppercase text-2xl flex items-center gap-2">
                <Clock className="h-6 w-6" />
                Interval Timer
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="space-y-6">
                {/* Drill Mode Banner */}
                {isDrillMode && activePlan && (
                  <div className="bg-primary/10 border border-primary/30 rounded-lg p-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-primary font-semibold uppercase">Drill Mode Active</p>
                        <p className="font-bold text-primary">{activePlan.title}</p>
                        <p className="text-sm text-muted-foreground">
                          Drill {currentDrillIndex + 1}/{activePlan.drills.length}: {activePlan.drills[currentDrillIndex]?.name}
                        </p>
                      </div>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={skipToNextDrill}
                        disabled={currentDrillIndex >= activePlan.drills.length - 1}
                        data-testid="skip-drill-btn"
                      >
                        Skip →
                      </Button>
                    </div>
                    {/* Drill progress bar */}
                    <div className="mt-2 flex gap-1">
                      {activePlan.drills.map((_, idx) => (
                        <div 
                          key={idx} 
                          className={`h-2 flex-1 rounded ${idx < currentDrillIndex ? 'bg-green-500' : idx === currentDrillIndex ? 'bg-primary' : 'bg-muted'}`}
                        />
                      ))}
                    </div>
                  </div>
                )}
                
                <div className="text-center">
                  <div className="text-7xl font-black font-mono mb-4">
                    {String(timerMinutes).padStart(2, '0')}:{String(timerSeconds).padStart(2, '0')}
                  </div>
                  <div className="flex gap-2 justify-center mb-4">
                    {!isTimerRunning ? (
                      <Button onClick={startTimer} data-testid="start-timer-btn">
                        <Play className="h-4 w-4 mr-2" />
                        START
                      </Button>
                    ) : (
                      <Button onClick={pauseTimer} variant="outline" data-testid="pause-timer-btn">
                        <Square className="h-4 w-4 mr-2" />
                        PAUSE
                      </Button>
                    )}
                    <Button onClick={resetTimer} variant="outline" data-testid="reset-timer-btn">
                      RESET
                    </Button>
                  </div>
                  <div className="flex items-center justify-center gap-4">
                    <Label>Minutes:</Label>
                    <Input
                      type="number"
                      min="0"
                      max="120"
                      value={timerMinutes}
                      onChange={(e) => setTimerMinutes(parseInt(e.target.value) || 0)}
                      disabled={isTimerRunning || isDrillMode}
                      className="w-20"
                      data-testid="timer-minutes-input"
                    />
                    <Label>Seconds:</Label>
                    <Input
                      type="number"
                      min="0"
                      max="59"
                      value={timerSeconds}
                      onChange={(e) => setTimerSeconds(Math.min(59, parseInt(e.target.value) || 0))}
                      disabled={isTimerRunning || isDrillMode}
                      className="w-20"
                      data-testid="timer-seconds-input"
                    />
                  </div>
                  <div className="flex items-center justify-center gap-2 mt-4">
                    <input
                      type="checkbox"
                      checked={alarmEnabled}
                      onChange={(e) => setAlarmEnabled(e.target.checked)}
                      data-testid="alarm-checkbox"
                    />
                    <Label className="flex items-center gap-2">
                      <Bell className="h-4 w-4" />
                      Enable Alarm
                    </Label>
                  </div>
                </div>
              </div>
              <audio ref={audioRef} src="data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBSmA0vLTgjMGHm7A7+OZRQ0PVqzn77BdGAg+ltry1n4qBCeA0fLNeSsFIXXH8OaYRAwQYLfp65xLEAk7l9vxxYIxBSh+0PLSgTQGHmzD7+OaRw4NVKzn8bJiGgk8lNrywYQxBSh+0PLSgTQGHmzD7+OaRw4NVKzn8bJiGgk8lNrywYQxBSh+0PLSgTQGHmzD7+OaRw4NVKzn8bJiGgk8lNrywYQxBSh+0PLSgTQGHmzD7+OaRw4NVKzn8bJiGgk8lNrywYQxBSh+0PLSgTQGHmzD7+OaRw4NVKzn8bJiGgk8lNrywYQxBSh+0PLSgTQGHmzD7+OaRw4NVKzn8bJiGgk8lNrywYQxBSh+0PLSgTQGHmzD7+OaRw4NVKzn8bJiGgk8lNrywYQxBSh+0PLSgTQGHmzD7+OaRw4NVKzn8bJiGgk8lNrywYQxBSh+0PLSgTQGHmzD7+OaRw4NVKzn8bJiGgk8lNrywYQxBSh+0PLSgTQGHmzD7+OaRw4NVKzn8bJiGgk8lNrywYQxBSh+0PLSgTQGHmzD7+OaRw4NVKzn8bJiGgk8lNrywYQxBSh+0PLSgTQGHmzD7+OaRw4NVKzn8bJiGgk8lNrywYQxBSh+0PLSgTQGHmzD7+OaRw4NVKzn8bJiGgk8lNrywYQxBSh+0PLSgTQGHmzD7+OaRw4NVKzn8bJiGgk8lNrywYQxBSh+0PLSgTQGHmzD7+OaRw4N" />
            </CardContent>
          </Card>

          {/* Record Practice */}
          <Card className="border-2">
            <CardHeader className="border-b bg-muted/30">
              <CardTitle className="font-heading uppercase text-2xl flex items-center gap-2">
                <Camera className="h-6 w-6" />
                Record Practice
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Record practice sessions with automatic AI analysis for player tracking and statistics.
                </p>
                <Dialog open={showRecordDialog} onOpenChange={setShowRecordDialog}>
                  <DialogTrigger asChild>
                    <Button className="w-full" data-testid="open-record-dialog-btn">
                      <Camera className="h-4 w-4 mr-2" />
                      START RECORDING
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-4xl">
                    <DialogHeader>
                      <DialogTitle className="font-heading text-2xl uppercase">Record Practice Session</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div className="aspect-video bg-black rounded-lg overflow-hidden">
                        <video
                          ref={videoPreviewRef}
                          autoPlay
                          muted
                          className="w-full h-full object-cover"
                          data-testid="video-preview"
                        />
                      </div>
                      <div className="flex gap-2 justify-center">
                        {!isRecording && recordedChunks.length === 0 && (
                          <Button onClick={startRecording} data-testid="start-recording-btn">
                            <Camera className="h-4 w-4 mr-2" />
                            START RECORDING
                          </Button>
                        )}
                        {isRecording && (
                          <Button onClick={stopRecording} variant="destructive" data-testid="stop-recording-btn">
                            <Square className="h-4 w-4 mr-2" />
                            STOP RECORDING
                          </Button>
                        )}
                        {recordedChunks.length > 0 && (
                          <>
                            <Button onClick={() => setRecordedChunks([])} variant="outline">
                              DISCARD
                            </Button>
                            <Button onClick={() => { setRecordedChunks([]); startRecording(); }} variant="outline">
                              RECORD AGAIN
                            </Button>
                          </>
                        )}
                      </div>
                      {recordedChunks.length > 0 && (
                        <div className="space-y-4">
                          <div>
                            <Label>Recording Title *</Label>
                            <Input
                              data-testid="recording-title"
                              value={recordingTitle}
                              onChange={(e) => setRecordingTitle(e.target.value)}
                              placeholder="Practice session title"
                            />
                          </div>
                          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                            <p className="text-sm font-medium text-blue-900">✨ AI Analysis Enabled</p>
                            <p className="text-xs text-blue-700 mt-1">
                              This recording will be automatically analyzed to track players, detect shots, and extract statistics.
                            </p>
                          </div>
                          <Button onClick={saveRecording} className="w-full" data-testid="save-recording-btn">
                            <Save className="h-4 w-4 mr-2" />
                            SAVE & ANALYZE WITH AI
                          </Button>
                        </div>
                      )}
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Practice Plans */}
        <Card className="border-2">
          <CardHeader className="border-b bg-muted/30 flex flex-row items-center justify-between">
            <CardTitle className="font-heading uppercase text-2xl flex items-center gap-2">
              <FileText className="h-6 w-6" />
              Practice Plans
            </CardTitle>
            {user.role !== 'player' && canEdit('practice_plans') && (
              <div className="flex items-center gap-2">
              <input ref={planFileRef} type="file" accept=".csv,.xlsx,.xls,.pdf,.doc,.docx" className="hidden" data-testid="plan-import-input"
                     onChange={handlePlanImport} />
              <Button variant="outline" data-testid="import-plan-btn" disabled={isImportingPlan} onClick={() => planFileRef.current?.click()}>
                <Upload className="h-4 w-4 mr-2" />
                {isImportingPlan ? 'IMPORTING…' : 'IMPORT PLAN'}
              </Button>
              <Dialog open={showAddPlanDialog} onOpenChange={(o) => { setShowAddPlanDialog(o); if (!o) { setEditingImport(false); setImportQueue([]); setImportTotal(0); } }}>
                <DialogTrigger asChild>
                  <Button data-testid="add-plan-btn">
                    <Plus className="h-4 w-4 mr-2" />
                    ADD PLAN
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="font-heading text-2xl uppercase">{editingImport ? 'Review Imported Plan' : 'Create Practice Plan'}</DialogTitle>
                    {editingImport && (
                      <p className="text-sm text-muted-foreground" data-testid="import-review-note">
                        Read from your file{importTotal > 1 ? ` (plan ${importTotal - importQueue.length} of ${importTotal})` : ''}. Check the title, fix any drill name, minutes or seconds, add or remove drills, then CREATE PLAN.
                      </p>
                    )}
                  </DialogHeader>
                  <form onSubmit={handleAddPlan} className="space-y-4">
                    <div>
                      <Label>Plan Title *</Label>
                      <Input
                        value={newPlan.title}
                        onChange={(e) => setNewPlan({...newPlan, title: e.target.value})}
                        required
                        placeholder="e.g., Pre-Season Conditioning"
                        data-testid="plan-title"
                      />
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Practice Objectives</Label>
                        <Textarea
                          value={newPlan.objectives}
                          onChange={(e) => setNewPlan({...newPlan, objectives: e.target.value})}
                          rows={2}
                          placeholder="What should players achieve?"
                          data-testid="plan-objectives"
                        />
                      </div>
                      <div>
                        <Label>Focus Area</Label>
                        <Select 
                          value={newPlan.focus_area} 
                          onValueChange={(v) => setNewPlan({...newPlan, focus_area: v})}
                        >
                          <SelectTrigger data-testid="plan-focus-area">
                            <SelectValue placeholder="Select focus area" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="offense">Offense</SelectItem>
                            <SelectItem value="defense">Defense</SelectItem>
                            <SelectItem value="conditioning">Conditioning</SelectItem>
                            <SelectItem value="shooting">Shooting</SelectItem>
                            <SelectItem value="ball_handling">Ball Handling</SelectItem>
                            <SelectItem value="team_plays">Team Plays</SelectItem>
                            <SelectItem value="scrimmage">Scrimmage</SelectItem>
                            <SelectItem value="mixed">Mixed</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {/* Drills Section */}
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <Label className="text-lg font-semibold">Drills ({newPlan.drills.length}/15)</Label>
                        {newPlan.drills.length < 15 && (
                          <Button 
                            type="button"
                            size="sm"
                            onClick={() => setNewPlan({
                              ...newPlan, 
                              drills: [...newPlan.drills, { name: '', duration: 5, type: 'drill' }]
                            })}
                            data-testid="add-drill-btn"
                          >
                            <Plus className="h-4 w-4 mr-1" />
                            Add Drill
                          </Button>
                        )}
                      </div>
                      
                      <div className="space-y-2 max-h-60 overflow-y-auto">
                        {newPlan.drills.map((drill, index) => (
                          <div key={index} className={`flex items-center gap-2 p-2 rounded border ${drill.type === 'break' ? 'bg-yellow-50 border-yellow-200' : 'bg-muted/30'}`}>
                            <span className="text-xs font-semibold w-6">{index + 1}.</span>
                            <Select 
                              value={drill.type} 
                              onValueChange={(v) => {
                                const updated = [...newPlan.drills];
                                updated[index].type = v;
                                if (v === 'break') updated[index].name = 'BREAK';
                                setNewPlan({...newPlan, drills: updated});
                              }}
                            >
                              <SelectTrigger className="w-24">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="drill">Drill</SelectItem>
                                <SelectItem value="break">Break</SelectItem>
                              </SelectContent>
                            </Select>
                            <Input
                              value={drill.name}
                              onChange={(e) => {
                                const updated = [...newPlan.drills];
                                updated[index].name = e.target.value;
                                setNewPlan({...newPlan, drills: updated});
                              }}
                              placeholder={drill.type === 'break' ? 'Break' : 'Drill name'}
                              className="flex-1"
                              disabled={drill.type === 'break'}
                            />
                            <Input
                              type="number"
                              min="1"
                              max="60"
                              value={drill.duration}
                              onChange={(e) => {
                                const updated = [...newPlan.drills];
                                updated[index].duration = parseInt(e.target.value) || 5;
                                setNewPlan({...newPlan, drills: updated});
                              }}
                              className="w-16"
                              data-testid={`drill-minutes-${index}`}
                            />
                            <span className="text-xs text-muted-foreground">min</span>
                            <Input
                              type="number"
                              min="0"
                              max="59"
                              value={drill.seconds || 0}
                              onChange={(e) => {
                                const updated = [...newPlan.drills];
                                updated[index].seconds = Math.min(59, Math.max(0, parseInt(e.target.value) || 0));
                                setNewPlan({...newPlan, drills: updated});
                              }}
                              className="w-16"
                              data-testid={`drill-seconds-${index}`}
                            />
                            <span className="text-xs text-muted-foreground">sec</span>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                const updated = newPlan.drills.filter((_, i) => i !== index);
                                setNewPlan({...newPlan, drills: updated});
                              }}
                            >
                              ✕
                            </Button>
                          </div>
                        ))}
                      </div>
                      
                      {newPlan.drills.length < 15 && (
                        <Button 
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setNewPlan({
                            ...newPlan, 
                            drills: [...newPlan.drills, { name: 'BREAK', duration: 5, type: 'break' }]
                          })}
                          className="w-full border-yellow-400 text-yellow-700 hover:bg-yellow-50"
                          data-testid="add-break-btn"
                        >
                          <Plus className="h-4 w-4 mr-1" />
                          Add Break
                        </Button>
                      )}
                      
                      {newPlan.drills.length > 0 && (
                        <div className="text-sm text-muted-foreground text-right">
                          Total Duration: {newPlan.drills.reduce((sum, d) => sum + d.duration, 0)} minutes
                        </div>
                      )}
                    </div>
                    
                    <div>
                      <Label>Additional Notes</Label>
                      <Textarea
                        value={newPlan.description}
                        onChange={(e) => setNewPlan({...newPlan, description: e.target.value})}
                        rows={3}
                        placeholder="Any additional instructions or notes..."
                        data-testid="plan-description"
                      />
                    </div>
                    {editingImport && (
                      <Button type="button" variant="outline" className="w-full" onClick={skipImportedPlan} data-testid="skip-imported-plan-btn">
                        {importQueue.length ? 'SKIP THIS PLAN' : 'DISCARD IMPORT'}
                      </Button>
                    )}
                    <Button type="submit" className="w-full" data-testid="submit-plan-btn">
                      CREATE PLAN
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
              </div>
            )}
          </CardHeader>
          <CardContent className="p-6">
            {practicePlans.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">No practice plans yet. Create your first plan!</p>
            ) : (
              <div className="space-y-4">
                {practicePlans.map((plan) => (
                  <div key={plan.id} className="p-4 border-2 rounded-lg hover:border-primary/50 transition-colors">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="font-heading text-xl font-bold uppercase">{plan.title}</h3>
                        {plan.focus_area && (
                          <Badge variant="secondary" className="capitalize mt-1">{plan.focus_area.replace('_', ' ')}</Badge>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => startDrillTimer(plan)}
                          data-testid={`start-drill-timer-${plan.id}`}
                          title="Start drill-by-drill timer"
                        >
                          <Play className="h-4 w-4 mr-1" />
                          Drill Mode
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => {
                            // Set timer from plan total duration
                            const totalMinutes = plan.drills?.reduce((sum, d) => sum + d.duration, 0) || 0;
                            setTimerMinutes(totalMinutes);
                            setTimerSeconds(0);
                            setIsDrillMode(false);
                            setActivePlan(null);
                            toast.success(`Timer set to ${totalMinutes} minutes`);
                          }}
                          data-testid={`use-timer-${plan.id}`}
                          title="Set total duration timer"
                        >
                          <Clock className="h-4 w-4 mr-1" />
                          {plan.drills?.reduce((sum, d) => sum + d.duration, 0) || 0}m
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => printPlan(plan)}
                          data-testid={`print-plan-${plan.id}`}
                        >
                          <FileText className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="destructive" 
                          size="sm"
                          onClick={() => deletePracticePlan(plan.id)}
                          data-testid={`delete-plan-${plan.id}`}
                          title="Delete practice plan"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    
                    {plan.objectives && (
                      <div className="mb-2">
                        <span className="text-xs font-semibold text-muted-foreground">OBJECTIVES:</span>
                        <p className="text-sm">{plan.objectives}</p>
                      </div>
                    )}
                    
                    {plan.drills && plan.drills.length > 0 && (
                      <div className="mb-3">
                        <span className="text-xs font-semibold text-muted-foreground">DRILLS:</span>
                        <div className="mt-1 space-y-1">
                          {plan.drills.map((drill, idx) => (
                            <div 
                              key={idx} 
                              className={`flex justify-between text-sm p-2 rounded ${drill.type === 'break' ? 'bg-yellow-50 text-yellow-700' : 'bg-muted/30'}`}
                            >
                              <span>{idx + 1}. {drill.name}</span>
                              <span className="font-mono">{fmtDrill(drill)}</span>
                            </div>
                          ))}
                          <div className="text-right text-sm font-semibold pt-1 border-t">
                            Total: {plan.drills.reduce((sum, d) => sum + d.duration, 0)} min
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {plan.description && (
                      <p className="text-sm text-muted-foreground whitespace-pre-wrap">{plan.description}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Practice Videos with AI Analysis */}
        <Card className="border-2">
          <CardHeader className="border-b bg-muted/30">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <CardTitle className="font-heading uppercase text-2xl flex items-center gap-2">
                <Video className="h-6 w-6" />
                Practice Recordings & AI Analysis
              </CardTitle>
              <div className="inline-flex rounded-md border overflow-hidden" data-testid="recording-view-toggle">
                <button
                  type="button"
                  onClick={() => setRecordingView('active')}
                  className={`px-3 py-1.5 text-xs font-semibold uppercase transition ${recordingView === 'active' ? 'bg-primary text-primary-foreground' : 'bg-transparent text-muted-foreground hover:bg-muted'}`}
                  data-testid="tab-recordings-active"
                >
                  Active ({practiceVideos.length})
                </button>
                <button
                  type="button"
                  onClick={() => setRecordingView('archived')}
                  className={`px-3 py-1.5 text-xs font-semibold uppercase transition border-l ${recordingView === 'archived' ? 'bg-amber-500 text-white' : 'bg-transparent text-muted-foreground hover:bg-muted'}`}
                  data-testid="tab-recordings-archived"
                >
                  Archived ({archivedVideos.length})
                </button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-6">
            {recordingView === 'active' ? (
              practiceVideos.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">No practice videos yet. Record your first practice!</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {practiceVideos.filter((v) => !v.archived).map((video) => (
                    <div key={video.id} className="border-2 rounded-lg overflow-hidden hover:border-primary/50 transition-colors" data-testid={`practice-video-${video.id}`}>
                      <div className="aspect-video bg-black">
                        {video.file_url ? (
                          <video
                            controls
                            className="w-full h-full"
                            preload="metadata"
                            poster={video.thumbnail_url ? `${BACKEND_URL}${video.thumbnail_url}` : undefined}
                            data-testid={`practice-video-player-${video.id}`}
                          >
                            <source src={`${BACKEND_URL}${video.file_url}`} />
                          Your browser can&apos;t play this recording.
                        </video>
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Play className="h-16 w-16 text-white/50" />
                        </div>
                      )}
                    </div>
                    <div className="p-4">
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <h3 className="font-semibold flex-1 min-w-0 truncate">{video.title}</h3>
                        <div className="flex items-center gap-1 shrink-0">
                          {canEdit('practice_plans') && (
                            <>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7 text-amber-600 hover:text-amber-700"
                                onClick={() => archivePracticeVideo(video.id)}
                                title="Archive"
                                data-testid={`archive-practice-video-${video.id}`}
                              >
                                <Archive className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7 text-red-500 hover:text-red-700"
                                onClick={() => deletePracticeVideo(video.id)}
                                title="Delete"
                                data-testid={`delete-practice-video-${video.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground mb-3">{video.description}</p>
                      {video.analysis_status && (
                        <div className="space-y-2">
                          <div className="flex items-center justify-between text-xs">
                            <span className="uppercase font-semibold">AI Analysis:</span>
                            <span className="capitalize">{video.analysis_status}</span>
                          </div>
                          {video.analysis_status === 'processing' && (
                            <Progress value={video.analysis_progress || 0} className="h-2" />
                          )}
                          {video.analysis_status === 'completed' && (
                            <Button variant="outline" size="sm" className="w-full">
                              <TrendingUp className="h-4 w-4 mr-2" />
                              VIEW STATISTICS
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              )
            ) : (
              archivedVideos.length === 0 ? (
                <p className="text-center text-muted-foreground py-8" data-testid="archived-empty">
                  No archived recordings.
                </p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="archived-grid">
                  {archivedVideos.map((video) => (
                    <div key={video.id} className="border-2 border-amber-300 rounded-lg overflow-hidden opacity-90" data-testid={`archived-video-${video.id}`}>
                      <div className="aspect-video bg-black">
                        {video.file_url ? (
                          <video
                            controls
                            className="w-full h-full"
                            preload="metadata"
                            poster={video.thumbnail_url ? `${BACKEND_URL}${video.thumbnail_url}` : undefined}
                          >
                            <source src={`${BACKEND_URL}${video.file_url}`} />
                            Your browser can&apos;t play this recording.
                          </video>
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Play className="h-16 w-16 text-white/50" />
                          </div>
                        )}
                      </div>
                      <div className="p-4">
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <div className="flex-1 min-w-0">
                            <span className="inline-block bg-amber-100 text-amber-800 text-[10px] uppercase font-bold px-2 py-0.5 rounded mr-2">
                              Archived
                            </span>
                            <h3 className="font-semibold inline">{video.title}</h3>
                          </div>
                          {canEdit('practice_plans') && (
                            <div className="flex items-center gap-1 shrink-0">
                              <Button
                                variant="outline"
                                size="sm"
                                className="border-orange-500 text-orange-500 hover:bg-orange-500 hover:text-white"
                                onClick={() => restorePracticeVideo(video.id)}
                                data-testid={`restore-practice-video-${video.id}`}
                                title="Restore"
                              >
                                <ArchiveRestore className="h-4 w-4 mr-1" />
                                Restore
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7 text-red-500 hover:text-red-700"
                                onClick={() => deletePracticeVideo(video.id)}
                                title="Delete permanently"
                                data-testid={`delete-archived-video-${video.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground">{video.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )
            )}
          </CardContent>
        </Card>

        {/* Scouting Reports */}
        <Card className="border-2">
          <CardHeader className="border-b bg-muted/30 flex flex-row items-center justify-between">
            <CardTitle className="font-heading uppercase text-2xl flex items-center gap-2">
              <FileText className="h-6 w-6" />
              Scouting Reports
            </CardTitle>
            {user.role !== 'player' && canEdit('practice_plans') && (
              <Dialog open={showAddReportDialog} onOpenChange={setShowAddReportDialog}>
                <DialogTrigger asChild>
                  <Button data-testid="add-report-btn">
                    <Plus className="h-4 w-4 mr-2" />
                    ADD REPORT
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl">
                  <DialogHeader>
                    <DialogTitle className="font-heading text-2xl uppercase">Create Scouting Report</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleAddReport} className="space-y-4">
                    <div>
                      <Label>Opponent Team</Label>
                      <Input
                        value={newReport.opponent_name}
                        onChange={(e) => setNewReport({...newReport, opponent_name: e.target.value})}
                        required
                        data-testid="report-opponent"
                      />
                    </div>
                    <div>
                      <Label>Strengths</Label>
                      <Textarea
                        value={newReport.strengths}
                        onChange={(e) => setNewReport({...newReport, strengths: e.target.value})}
                        rows={3}
                        data-testid="report-strengths"
                      />
                    </div>
                    <div>
                      <Label>Weaknesses</Label>
                      <Textarea
                        value={newReport.weaknesses}
                        onChange={(e) => setNewReport({...newReport, weaknesses: e.target.value})}
                        rows={3}
                        data-testid="report-weaknesses"
                      />
                    </div>
                    <div>
                      <Label>Game Plan</Label>
                      <Textarea
                        value={newReport.game_plan}
                        onChange={(e) => setNewReport({...newReport, game_plan: e.target.value})}
                        rows={4}
                        data-testid="report-gameplan"
                      />
                    </div>
                    <Button type="submit" className="w-full" data-testid="submit-report-btn">
                      CREATE REPORT
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            )}
          </CardHeader>
          <CardContent className="p-6">
            {scoutingReports.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">No scouting reports yet. Create your first report!</p>
            ) : (
              <div className="space-y-4">
                {scoutingReports.map((report) => (
                  <div key={report.id} className="p-4 border-2 rounded-lg hover:border-primary/50 transition-colors">
                    <h3 className="font-heading text-xl font-bold uppercase mb-3">vs. {report.opponent_name || report.opponent}</h3>
                    <Tabs defaultValue="strengths" className="w-full">
                      <TabsList className="grid w-full grid-cols-3">
                        <TabsTrigger value="strengths">Strengths</TabsTrigger>
                        <TabsTrigger value="weaknesses">Weaknesses</TabsTrigger>
                        <TabsTrigger value="gameplan">Game Plan</TabsTrigger>
                      </TabsList>
                      <TabsContent value="strengths" className="text-sm whitespace-pre-wrap">
                        {report.strengths || 'No information'}
                      </TabsContent>
                      <TabsContent value="weaknesses" className="text-sm whitespace-pre-wrap">
                        {report.weaknesses || 'No information'}
                      </TabsContent>
                      <TabsContent value="gameplan" className="text-sm whitespace-pre-wrap">
                        {report.game_plan || 'No information'}
                      </TabsContent>
                    </Tabs>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
        </>
        )}
      </div>
    </Layout>
  );
}